/**
 * The CMS `page` query of `ContentPage` (home, content pages, not found), in one place: `ContentPage` runs it,
 * the SSR middleware prefetches it (`server/middlewares/reactRenderer/urls.ts`) and `bootstrapSSR` seeds its
 * result in React Query under the key `ContentPage` reads, so the page is requested once.
 *
 * Pure module with relative imports only: the server code imports it without the aliases.
 */
import {applyCmsSiteFilter, buildCmsContentQueryKey, CmsQueryConfig} from '../../hooks/useCmsContent/buildCmsQueryKey';

export const CONTENT_PAGE_CONTENT_TYPE = 'page';
export const CONTENT_PAGE_COLLECTION_KEY = 'pageCollection';

/** Inputs of the page query: same values on the server (middleware) and in `ContentPage`. */
export interface ContentPageCmsQueryInput {
	/** Path without the locale, `/` for the home page. */
	slug: string;
	/** Language of the URL (`it` for `/it-it/...`). */
	slugLocale: string;
	/** Default language of the site: when different from `slugLocale`, the slug is also searched in it. */
	defaultLocale: string;
	/** `app.cmsAPI.multiSiteEnabled.pages`: the query is filtered by site (`useCmsContent` `filterSite`). */
	multiSitePages: boolean;
	/** Site of the request, used by the site filter (`[siteId, 'all']`). */
	siteId?: string;
}

/**
 * Slugs searched for a page: the slug and the same slug with/without the trailing slash (`/` alone).
 */
export function getContentPageSlugs(slug: string): string[] {
	const slugs = [slug];

	if (slug && slug !== '/') {
		slugs.push(slug.endsWith('/') ? slug.substring(0, slug.length - 1) : `${slug}/`);
	}

	return slugs;
}

type ContentPageVariables = {
	slug: Record<string, string[]>;
	allLocales: boolean;
};

const buildVariables = (slug: string, locale: string): ContentPageVariables => ({
	slug: {[locale]: getContentPageSlugs(slug)},
	allLocales: true,
});

/**
 * `useCmsContent` options of the page query, as `ContentPage` passes them (site filter not applied yet:
 * `useCmsContent` adds it from `filterSite`).
 */
export function getContentPageCmsQuery({slug, slugLocale, defaultLocale, multiSitePages}: Omit<ContentPageCmsQueryInput, 'siteId'>) {
	const config: {variables: ContentPageVariables; fallbackVariables: ContentPageVariables[]} = {
		variables: buildVariables(slug, slugLocale),
		fallbackVariables: slugLocale !== defaultLocale ? [buildVariables(slug, defaultLocale)] : [],
	};

	return {
		contentType: CONTENT_PAGE_CONTENT_TYPE,
		config,
		collectionKey: CONTENT_PAGE_COLLECTION_KEY,
		filterSite: !!multiSitePages,
	};
}

/**
 * Config the CMS provider receives for the page query: `ContentPage`'s config with the site filter of
 * `useCmsContent` applied.
 */
export function getContentPageCmsConfig(input: ContentPageCmsQueryInput): CmsQueryConfig {
	const {config, filterSite} = getContentPageCmsQuery(input);

	return filterSite ? applyCmsSiteFilter(config, input.siteId) : config;
}

/**
 * React Query key of the page query (`useSuspenseCmsContent` in `ContentPage`), for the SSR seed.
 */
export function getContentPageCmsQueryKey(providerId: string, input: ContentPageCmsQueryInput): readonly unknown[] {
	return buildCmsContentQueryKey({
		contentType: CONTENT_PAGE_CONTENT_TYPE,
		providerId,
		config: getContentPageCmsConfig(input),
		// `allLocales` query: the language is not part of the key.
		currentLanguage: null,
	});
}
